import {
  FCFS_CLOSE_HOUR,
  FCFS_CLOSE_WEEKDAY,
  REGULAR_SEASON_WEEKS,
  TOTAL_FANTASY_WEEKS,
  WEEK_1_SCORING_START_LABEL,
  getNowEastern,
  getWeek1ScoringStartsEastern,
} from "./seasonCalendar";

const WEEKDAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Spell out FCFS on first mention in user-facing copy. */
export const FCFS_LABEL = "first come, first served (FCFS)";

export function normalizeUnlockDay(day) {
  const d = (day || "wednesday").toLowerCase();
  return d === "tuesday" ? "tuesday" : "wednesday";
}

export function formatUnlockHour(hour, { includeTimezone = true } = {}) {
  const h = hour != null ? Number(hour) : 5;
  const am = h < 12;
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const suffix = includeTimezone ? " ET" : "";
  return `${display}:00 ${am ? "AM" : "PM"}${suffix}`;
}

export function formatUnlockDayLabel(day) {
  const key = normalizeUnlockDay(day);
  return key.charAt(0).toUpperCase() + key.slice(1);
}

export function resolveUnlockSettings(leagueSettings) {
  return {
    unlockDay: leagueSettings?.rosterUnlockDay || "wednesday",
    unlockHour: leagueSettings?.rosterUnlockHour != null ? leagueSettings.rosterUnlockHour : 5,
  };
}

export function isPreseasonTransactionPeriod(now = getNowEastern()) {
  const seasonStart = getWeek1ScoringStartsEastern();
  return Boolean(seasonStart && now < seasonStart);
}

export function getFcfsCloseLabel({ eastern = true } = {}) {
  const day = WEEKDAY_NAMES[FCFS_CLOSE_WEEKDAY];
  const am = FCFS_CLOSE_HOUR < 12;
  const display =
    FCFS_CLOSE_HOUR === 0 ? 12 : FCFS_CLOSE_HOUR > 12 ? FCFS_CLOSE_HOUR - 12 : FCFS_CLOSE_HOUR;
  const tz = eastern ? " Eastern" : " ET";
  return `${day} ${display}:00 ${am ? "AM" : "PM"}${tz}`;
}

export function getFcfsCloseShortLabel() {
  return "Sunday 1:00 PM ET";
}

function unlockWindowLabel(unlockDay, unlockHour) {
  return `${formatUnlockDayLabel(unlockDay)} ${formatUnlockHour(unlockHour, { includeTimezone: false })} Eastern`;
}

function claimQueueWindowLabel(unlockDay, unlockHour) {
  return `${getFcfsCloseShortLabel().replace(" ET", " Eastern")} – ${unlockWindowLabel(unlockDay, unlockHour)}`;
}

function waiverProcessingPhrase(unlockDay, unlockHour, { includeFaab = false, useWaiverPriority = true } = {}) {
  const dayLabel = formatUnlockDayLabel(unlockDay);
  const timeLabel = formatUnlockHour(unlockHour);
  if (includeFaab) {
    return `processed ${dayLabel} at ${timeLabel}`;
  }
  if (useWaiverPriority) {
    return `processed ${dayLabel} at ${timeLabel} by waiver priority`;
  }
  return `processed ${dayLabel} at ${timeLabel}`;
}

/** After draft until Week 1 kickoff — same rule for traditional and FAAB leagues. */
export function getPreseasonWaiverCopy() {
  return (
    `After your draft until Week 1 scoring begins (${WEEK_1_SCORING_START_LABEL}), all leagues use ` +
    `${FCFS_LABEL} pickups — immediate add/drop with no waiver queue and no FAAB bidding, regardless of waiver type. ` +
    "Post-drop waiver holds do not apply during this period."
  );
}

/** Post-drop hold copy for League Settings (in-season only). */
export function getPostDropWaiverSettingsCopy() {
  return (
    "After Week 1 scoring begins, when a player is dropped they stay on waivers for this many days " +
    "before returning to the free-agent pool (traditional and FAAB leagues). Not applied during the preseason FCFS window."
  );
}

/**
 * Footnote for waiver timing copy — private leagues can customize unlock; FCFS close is fixed.
 */
export function getWaiverCustomizationNote({ leagueSettings = null } = {}) {
  const fcfsFixed =
    "Sunday 1:00 PM ET first come, first served (FCFS) close is the same for all leagues.";
  if (!leagueSettings) {
    return (
      "Waiver run times shown use platform defaults (Wednesday 5:00 AM ET). " +
      "Private league commissioners can set roster unlock to Tuesday or Wednesday and choose the Eastern hour in League Settings. " +
      fcfsFixed
    );
  }
  if (leagueSettings.isPublic) {
    return `This public league uses the platform default waiver schedule. ${fcfsFixed}`;
  }
  return (
    "This league's waiver run times are shown above. Private league commissioners can change roster unlock day and hour in League Settings. " +
    fcfsFixed
  );
}

/**
 * Core pickup / claim-queue timing copy (no lineup note).
 */
export function getTransactionWindowCopy({
  unlockDay = "wednesday",
  unlockHour = 5,
  includeFaab = false,
  useWaiverPriority = true,
} = {}) {
  const fcfsOpen = unlockWindowLabel(unlockDay, unlockHour);
  const fcfsClose = getFcfsCloseLabel();
  const claimWindow = claimQueueWindowLabel(unlockDay, unlockHour);
  const claimTargets = includeFaab ? "waiver claims and FAAB bids" : "waiver claims";
  const processed = waiverProcessingPhrase(unlockDay, unlockHour, { includeFaab, useWaiverPriority });

  return (
    `Once the season starts, free-agent pickups are ${FCFS_LABEL} from ${fcfsOpen} – ${fcfsClose}. ` +
    `${claimWindow} you can queue ${claimTargets} (${processed}).`
  );
}

export function getLineupAndTransactionInfoCopy({
  unlockDay = "wednesday",
  unlockHour = 5,
  includeFaab = false,
  useWaiverPriority = true,
  lineupPrefix = "Change starters until each slot's score reveals at kickoff.",
} = {}) {
  return `${lineupPrefix} ${getPreseasonWaiverCopy()} ${getTransactionWindowCopy({
    unlockDay,
    unlockHour,
    includeFaab,
    useWaiverPriority,
  })}`;
}

/** League Settings waiver timing blurb (matches commissioner UI). */
export function getLeagueSettingsWaiverCopy({ unlockDay = "wednesday", unlockHour = 5, useFaab = false } = {}) {
  const dayLabel = formatUnlockDayLabel(unlockDay);
  const timeLabel = formatUnlockHour(unlockHour);
  const fcfsCloseShort = getFcfsCloseShortLabel();
  const preseason = getPreseasonWaiverCopy();
  if (useFaab) {
    return (
      `${preseason} In season: claims queue Sun 1:00 PM – ${dayLabel} ${timeLabel}. ` +
      `${FCFS_LABEL} pickups reopen after processing until ${fcfsCloseShort}. ` +
      "FAAB bids process automatically at the waiver run; commissioner can re-run Process Bids if needed."
    );
  }
  return (
    `${preseason} In season: claims queue Sun 1:00 PM – ${dayLabel} ${timeLabel}. ` +
    `Pending claims process at that time by waiver priority; ${FCFS_LABEL} pickups reopen until ${fcfsCloseShort}.`
  );
}

export function getPlatformDefaultWaiverSummary({ useFaab = false } = {}) {
  return getLeagueSettingsWaiverCopy({ unlockDay: "wednesday", unlockHour: 5, useFaab });
}

/** Short at-a-glance bullets for the season calendar banner. */
export function getSeasonCalendarBulletItems() {
  return [
    `Week 1 scoring: ${WEEK_1_SCORING_START_LABEL} (ET) — fantasy weeks run Thursday through Monday night`,
    `Regular season: weeks 1–${REGULAR_SEASON_WEEKS} · Playoffs: weeks 15–${TOTAL_FANTASY_WEEKS} (champion by Week ${TOTAL_FANTASY_WEEKS})`,
    "Draft before Week 1 scoring for a full regular season",
  ];
}

/** One-line waiver summary for league home (uses this league's unlock settings). */
export function getLeagueWaiverSummaryLine(leagueSettings) {
  const { unlockDay, unlockHour } = resolveUnlockSettings(leagueSettings);
  const dayLabel = formatUnlockDayLabel(unlockDay);
  const timeLabel = formatUnlockHour(unlockHour);
  const useFaab = Boolean(leagueSettings?.useFAAB);
  const processing = useFaab ? "FAAB processing" : "Waiver processing";
  const isWednesdayFive =
    normalizeUnlockDay(unlockDay) === "wednesday" && Number(unlockHour) === 5;
  let schedule;
  if (isWednesdayFive) {
    schedule = leagueSettings?.isPublic
      ? "Wednesday 5:00 AM ET (platform default)"
      : "Wednesday 5:00 AM ET";
  } else {
    schedule = `${dayLabel} ${timeLabel}`;
  }

  return `This league: ${FCFS_LABEL} until Sunday 1:00 PM ET; ${processing} ${schedule}.`;
}
